/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import Loadable from 'react-loadable';
import { Route, Switch, Redirect } from 'react-router-dom';
import Store from '../store/store';
import CadenceWall from '../components/Cadences/wall';
import DeploymentsWall from '../components/Deployments/wall';
import TargetstsWall from '../components/Targets/wall';
import Header from '../components/Header/header';

const userLoggedIn = () => Store.getState().userReducer.isLoggedIn;

const MyLoadingComponent = ({ isLoading, error }) => {
  document.title = 'Loading...';

  if (isLoading) {
    return <div id="loader" />;
  }

  if (error) {
    return <div>Sorry, there was a problem loading the page.</div>;
  }

  return null;
};

MyLoadingComponent.propTypes = {
  isLoading: PropTypes.bool,
  error: PropTypes.any
};

const AsyncLogin = Loadable({
  loader: () => System.import('../components/Auth/login'),
  loading: MyLoadingComponent
});

const AsyncResetPassword = Loadable({
  loader: () => System.import('../components/Auth/resetPassword'),
  loading: MyLoadingComponent
});

const AsyncCardWall = Loadable({
  loader: () => System.import('../components/CardWall/index'),
  loading: MyLoadingComponent
});

const AsyncKanbanWall = Loadable({
  loader: () => System.import('../components/Kanban/wall'),
  loading: MyLoadingComponent
});

const AsyncPlanWall = Loadable({
  loader: () => System.import('../components/PlanWall/index'),
  loading: MyLoadingComponent
});

const AsyncTeamWall = Loadable({
  loader: () => System.import('../components/Teams/wall'),
  loading: MyLoadingComponent
});

const PrivateRoute = ({ component: Component, ...rest }) => (
  <Route
    {...rest}
    render={(props) =>
      userLoggedIn() ? (
        <Component {...props} />
      ) : (
        <Redirect
          to={{ pathname: '/login', state: { from: props.location } }}
        />
      )
    }
  />
);

PrivateRoute.propTypes = {
  component: PropTypes.func,
  location: PropTypes.object
};

const CardWallRoutes = () => (
  <Switch>
    <Route path="/cardwall/kanban/r/:level" component={AsyncKanbanWall} />
    <Route path="/cardwall/cadences/:id/:view" component={AsyncCardWall} />
    <Route path="/cardwall/:view" component={AsyncCardWall} />
    <Route path="/cardwall" component={AsyncCardWall} />
  </Switch>
);

const PlanWallRoutes = () => (
  <Switch>
    <Route exact path="/planwall/r/:level" component={AsyncPlanWall} />
    <Route
      exact
      path="/planwall/o/:id/teams/:level"
      component={AsyncTeamWall}
    />
    <Redirect from="/planwall" to="/planwall/r/1" />
  </Switch>
);

const Main = () => (
  <>
    <Header />
    <Switch>
      <Route path="/cardwall" component={CardWallRoutes} />
      <Route path="/planwall" component={PlanWallRoutes} />
      <Route path="/cadences" component={CadenceWall} />
      <Route path="/deployments" component={DeploymentsWall} />
      <Route path="/targets" component={TargetstsWall} />
      <Redirect from="/" to="/cardwall" />
    </Switch>
  </>
);

export default (
  <Switch>
    <Route exact path="/login" component={AsyncLogin} />
    <Route exact path="/reset_password" component={AsyncResetPassword} />
    <PrivateRoute path="/" component={Main} />
  </Switch>
);
